import type React from 'react';

import type * as api from '@gitbook/api-client';
import { type PaletteCommandDef, PaletteCommandType } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { Alert, Button, confirm, toast, Tooltip } from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';
import { assertNever } from '@gitbook/util-ts';

import {
    excludeSiteExternalLinks,
    flattenGroupedSections,
    getSiteSectionGroupChildren,
    getSiteSectionsFromSiteSectionGroup,
    type SiteStructureContent,
} from '../../../state';
import { updateSiteContentDraftState } from './updateSiteContentDraftState';

export type UseSiteContentDraftProps = {
    organization: api.Organization;
    site: api.Site;
    siteStructure: api.SiteStructure;
    content: SiteStructureContent;
    canCreateSite: boolean;
};

type SiteContentDraftProps = {
    siteItemType: SiteStructureContent['object'];
    draft: boolean;
    label: 'variant' | 'section' | 'group' | 'external link';
    disabledReason: string | undefined;
    toggleDraft: (draft: boolean) => Promise<void>;
};

/**
 * Return the shared draft toggle props for a site structure item.
 */
export function useSiteContentDraft(props: UseSiteContentDraftProps): SiteContentDraftProps {
    const { organization, site, siteStructure, content } = props;

    const label = getSiteContentLabel(content);
    const disabledReason = getDraftToggleDisabledReason(props);

    const toggleDraft = useEventCallback(async (draft: boolean) => {
        // Ask for confirmation before applying the draft toggle.
        await confirm({
            title: draft ? 'Mark as draft' : 'Undraft',
            description: getDraftToggleConfirmDescription({
                draft,
                label,
                site,
                siteStructure,
                content,
            }),
            confirmLabel: 'Confirm',
            onConfirm: async (_event, { preventClose }) => {
                await toast.catch(
                    async () => {
                        await updateSiteContentDraftState({
                            organizationId: organization.id,
                            siteId: site.id,
                            content,
                            draft,
                        });
                    },
                    `Error while updating ${label} draft state`,
                    { onReject: preventClose }
                );
            },
        });
    });

    return {
        siteItemType: content.object,
        draft: content.draft,
        label,
        disabledReason,
        toggleDraft,
    };
}

/**
 * Return the confirmation description shown before toggling a site content draft state.
 */
export function getDraftToggleConfirmDescription(props: {
    draft: boolean;
    label: SiteContentDraftProps['label'];
    site: api.Site;
    siteStructure: api.SiteStructure;
    content: SiteStructureContent;
}): React.ReactNode {
    const { draft, label, site, siteStructure, content } = props;
    const description = draft
        ? site.published
            ? `This ${label} won't be visible on your public site anymore. You can always undraft it later to make it visible again.`
            : `Draft ${label} won't be visible on your site when published.`
        : site.published
          ? `This ${label} will be visible on your published site right away. You can always revert it to draft later.`
          : `This ${label} will be visible on your site when published.`;
    const warnings = getDraftToggleWarnings({ draft, content, siteStructure });

    if (warnings.length === 0) {
        return description;
    }

    return (
        <div className="space-y-4">
            <p>{description}</p>
            <Alert
                kind="warning"
                size="small"
                title="This will affect your published content visibility"
                details={<DraftToggleWarningDetails warnings={warnings} />}
            />
        </div>
    );
}

function DraftToggleWarningDetails(props: { warnings: string[] }): React.ReactElement {
    const { warnings } = props;

    if (warnings.length === 1) {
        return <>{warnings[0]}</>;
    }

    return (
        <div className="space-y-1">
            {warnings.map((warning) => (
                <p key={warning}>{warning}</p>
            ))}
        </div>
    );
}

/**
 * Create the draft palette command for a site structure item.
 */
export function useSiteContentDraftCommand(props: UseSiteContentDraftProps): PaletteCommandDef {
    const { siteItemType, draft, disabledReason, toggleDraft } = useSiteContentDraft(props);

    return {
        key: `${siteItemType}.draft`,
        title: 'Draft',
        type: PaletteCommandType.SWITCH,
        enabled: draft,
        icon: Icon.Draft,
        disabled: Boolean(disabledReason),
        subtitle: disabledReason,
        trigger: () => {
            void toggleDraft(!draft);
        },
    };
}

/**
 * Render the draft toggle button for a site structure editor item.
 */
export function SiteContentDraftButton(props: UseSiteContentDraftProps): React.ReactElement {
    const { draft, disabledReason, toggleDraft } = useSiteContentDraft(props);

    const button = (
        <Button
            kind="secondary"
            size="small"
            label={draft ? 'Undraft' : 'Mark as draft'}
            leadingIcon={Icon.Draft}
            disabled={Boolean(disabledReason)}
            onPress={() => {
                void toggleDraft(!draft);
            }}
        />
    );

    return disabledReason ? (
        <Tooltip tooltip={disabledReason}>
            <span>{button}</span>
        </Tooltip>
    ) : (
        button
    );
}

function getSiteContentLabel(
    content: SiteStructureContent
): 'variant' | 'section' | 'group' | 'external link' {
    switch (content.object) {
        case 'site-space':
            return 'variant';
        case 'site-section':
            return 'section';
        case 'site-section-group':
            return 'group';
        case 'site-external-link':
            return 'external link';
        default:
            return assertNever(content);
    }
}

/**
 * Return warning messages for draft state changes that can affect site visibility.
 */
export function getDraftToggleWarnings(props: {
    draft: boolean;
    content: SiteStructureContent;
    siteStructure: api.SiteStructure;
}): string[] {
    const { draft, content, siteStructure } = props;
    if (!draft) {
        const draftParent =
            siteStructure.type === 'sections'
                ? findDraftParentInItems(
                      content,
                      excludeSiteExternalLinks(siteStructure.structure),
                      []
                  )
                : undefined;
        return draftParent
            ? [
                  `This ${getSiteContentLabel(content)} won't be visible on your site while its parent ${getSiteContentLabel(draftParent)} is draft.`,
              ]
            : [];
    }

    switch (content.object) {
        case 'site-space': {
            if (siteStructure.type === 'siteSpaces') {
                const liveSiteSpaces = siteStructure.structure.filter(
                    (siteSpace) => !siteSpace.draft
                );
                return content.default || liveSiteSpaces.length <= 1
                    ? [
                          'This can make your site display a 404 because the home variant will be draft.',
                      ]
                    : [];
            }

            const parentSection = getSiteSectionForSiteSpace(siteStructure, content);
            if (!parentSection) {
                return [];
            }

            const warnings: string[] = [];
            if (content.default || isOnlyLiveSiteSpaceInSection(parentSection, content)) {
                warnings.push(
                    `This can make the ${parentSection.default ? 'site and this section' : 'section'} display a 404 because the home variant will be draft.`
                );
            }
            return warnings;
        }
        case 'site-section': {
            if (siteStructure.type !== 'sections') {
                return [];
            }

            const liveSiteSections = flattenGroupedSections(siteStructure.structure).filter(
                (siteSection) => !siteSection.draft
            );
            const warnings: string[] = [];
            if (content.default || liveSiteSections.length <= 1) {
                warnings.push(
                    'This can make your site display a 404 because the home section will be draft.'
                );
            }
            const hasLiveSiteSpaces = content.siteSpaces.some((siteSpace) => !siteSpace.draft);
            if (content.siteSpaces.length > 1 && hasLiveSiteSpaces) {
                warnings.push(
                    'Live variants in this section will stay live, but they will not be visible while the section is draft.'
                );
            }
            return warnings;
        }
        case 'site-section-group': {
            if (siteStructure.type !== 'sections') {
                return [];
            }

            const hasHomeSectionInGroup = getSiteSectionsFromSiteSectionGroup(content).some(
                (siteSection) => siteSection.default
            );
            const warnings: string[] = [];
            if (hasHomeSectionInGroup) {
                warnings.push(
                    'This can make your site display a 404 because the home section will be inside a draft group.'
                );
            }
            if (getSiteSectionsFromSiteSectionGroup(content).some(hasLiveContentInSection)) {
                warnings.push(
                    'Live content in this group will stay live, but it will not be visible while the group is draft.'
                );
            }
            return warnings;
        }
        case 'site-external-link':
            return [];
        default:
            return assertNever(content);
    }
}

/**
 * Return why the draft toggle is unavailable, or undefined when it can be used.
 */
function getDraftToggleDisabledReason(props: UseSiteContentDraftProps): string | undefined {
    const { site, siteStructure, content, canCreateSite } = props;

    if (!canCreateSite) {
        return 'Only site creators and administrators can change draft status.';
    }
    if (site.published && !content.draft && wouldHideHomeSection(content, siteStructure)) {
        return "The home section can't be inside a draft group.";
    }
    return undefined;
}

/**
 * Whether drafting the content would put a visible home section inside a draft group.
 * A home section already hidden by a draft group doesn't count, so such a site stays fixable.
 */
function wouldHideHomeSection(
    content: SiteStructureContent,
    siteStructure: api.SiteStructure
): boolean {
    if (content.object !== 'site-section-group' || siteStructure.type !== 'sections') {
        return false;
    }

    const homeSection = getSiteSectionsFromSiteSectionGroup(content).find(
        (siteSection) => siteSection.default
    );
    return (
        homeSection !== undefined &&
        !findDraftParentInItems(homeSection, excludeSiteExternalLinks(siteStructure.structure), [])
    );
}

function hasLiveContentInSection(siteSection: api.SiteSection): boolean {
    return !siteSection.draft || siteSection.siteSpaces.some((siteSpace) => !siteSpace.draft);
}

function getSiteSectionForSiteSpace(
    siteStructure: Extract<api.SiteStructure, { type: 'sections' }>,
    siteSpace: api.SiteSpace
): api.SiteSection | undefined {
    return flattenGroupedSections(siteStructure.structure).find((siteSection) =>
        siteSection.siteSpaces.some((sectionSiteSpace) => sectionSiteSpace.id === siteSpace.id)
    );
}

function isOnlyLiveSiteSpaceInSection(
    siteSection: api.SiteSection,
    siteSpace: api.SiteSpace
): boolean {
    const liveSiteSpaces = siteSection.siteSpaces.filter(
        (sectionSiteSpace) => !sectionSiteSpace.draft
    );

    return liveSiteSpaces.length === 1 && liveSiteSpaces[0].id === siteSpace.id;
}

function findDraftParentInItems(
    content: SiteStructureContent,
    items: SiteStructureContent[],
    parents: (api.SiteSection | api.SiteSectionGroup)[]
): api.SiteSection | api.SiteSectionGroup | undefined {
    for (const item of items) {
        if (item.id === content.id && item.object === content.object) {
            return parents.findLast((parent) => parent.draft);
        }

        switch (item.object) {
            case 'site-space':
            case 'site-external-link':
                break;
            case 'site-section': {
                const draftParent = findDraftParentInItems(content, item.siteSpaces, [
                    ...parents,
                    item,
                ]);
                if (draftParent) {
                    return draftParent;
                }
                break;
            }
            case 'site-section-group': {
                const nextParents = [...parents, item];
                const draftParent = findDraftParentInItems(
                    content,
                    getSiteSectionGroupChildren(item),
                    nextParents
                );
                if (draftParent) {
                    return draftParent;
                }
                break;
            }
            default:
                assertNever(item);
        }
    }

    return undefined;
}
